import { FC, useCallback, useState } from 'react';

import { Order } from '@app/api/models/order';
import { RequestPayload } from '@app/api/types';
import { OrderListPayload } from '@app/api/orders';
import { Dialog } from '@app/components/common';
import { QueryTable } from '@app/components/common/table';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useApi, useFetchData } from '@app/hooks';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';

import { useColumns } from './columns';
import { OrderForm } from './form';

const OrderDialog: FC<{
  open: boolean;
  order?: Order;
  canChange: boolean;
  onClose: () => void;
}> = ({ open, order, canChange, onClose }) => {
  const { refetch } = useQueryTable();
  if (!order) {
    return null;
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Заказ #${order.pid}`}
      width={800}
      footerless
    >
      {open && (
        <OrderForm
          order={order}
          canChange={canChange}
          onSubmit={() => {
            refetch();
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </Dialog>
  );
};

export const OrdersTable: FC = () => {
  const api = useApi();
  const canChange = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });
  const [selected, setSelected] = useState<Order>();
  const [open, setOpen] = useState(false);

  const onOpen = useCallback((order: Order) => {
    setSelected(order);
    setOpen(true);
  }, []);

  const columns = useColumns({ onOpen, canChange });

  const fetchOrders = useCallback(
    (payload: RequestPayload) => {
      const raw = (payload.filter || {}) as Record<string, string | string[] | number>;
      const filter: OrderListPayload['filter'] = {};

      if (raw.distributor_id) {filter.distributor_id = Number(raw.distributor_id);}
      if (raw.outlet_id) {filter.outlet_id = Number(raw.outlet_id);}
      if (raw.status) {filter.status = String(raw.status);}
      if (raw.username) {filter.username = String(raw.username);}
      if (raw.created_b) {filter.created_from = String(raw.created_b);}
      if (raw.created_e) {filter.created_to = String(raw.created_e);}

      return api.orders.getOrders({
        limit: payload.limit,
        offset: payload.offset,
        sort: payload.sort,
        filter,
      });
    },
    [api]
  );

  const [fetchData] = useFetchData<Order>({ method: fetchOrders });

  return (
    <QueryTable<Order>
      title="Заказы"
      columns={columns}
      fetchData={fetchData}
      keyProperty="pid"
      initialSort={{ name: 'pid', direction: 'desc' }}
    >
      <OrderDialog
        open={open}
        order={selected}
        canChange={canChange}
        onClose={() => setOpen(false)}
      />
    </QueryTable>
  );
};

export default OrdersTable;
